import type { BuiltPrompt, VisualInterpretation } from './types';

const normalizeSentence = (value: string): string => value
  .replace(/\s+/g, ' ')
  .replace(/\s+,/g, ',')
  .replace(/,\s*,/g, ', ')
  .trim();

const buildCoreScene = (visual: VisualInterpretation): string => normalizeSentence([
  visual.subject,
  visual.action,
  visual.environment,
].filter(Boolean).join(', '));

export const buildProfessionalPrompt = (visual: VisualInterpretation): BuiltPrompt => {
  const coreScene = buildCoreScene(visual);
  const lighting = normalizeSentence(visual.timeLighting || '');
  const style = normalizeSentence(visual.moodStyle || '');

  // Build a flowing, cinematic sentence instead of a generic keyword list
  const sceneParts: string[] = [coreScene];

  if (lighting) sceneParts.push(lighting);
  if (style) sceneParts.push(`${style} atmosphere`);

  // Add cinematic depth only when we have enough scene info
  const hasSubject = Boolean(visual.subject?.trim());
  const hasEnv = Boolean(visual.environment?.trim());
  if (hasSubject && hasEnv) {
    sceneParts.push('fine scene detail');
    sceneParts.push('depth-aware focus');
  } else if (hasSubject) {
    sceneParts.push('sharp focus on subject');
  }

  const prompt = normalizeSentence(sceneParts.join(', '));

  return {
    coreScene,
    cameraSettings: visual.action ? 'scene-matched camera movement' : 'scene-matched framing',
    composition: hasSubject && hasEnv ? 'composition adapted to scene flow' : 'composition adapted to subject',
    lightingStyle: lighting || 'natural lighting',
    renderingQuality: 'cinematic realism',
    visualStyle: style || 'photorealistic',
    prompt,
  };
};
